import { matchPath } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import {
  CommunicationsContact,
  CommunicationsControl,
  CommunicationsCtaType,
  CommunicationsDetailRow,
  CommunicationsDetails,
  CommunicationsInlineAnchor,
  CommunicationsInlineItem,
  CommunicationsMessage,
  CommunicationsScreenKey,
  CommunicationsScreens
} from './communications.model';
import { COMMUNICATIONS_CTA_TYPES } from './constants';

export const getCommunicationsScreenKeyFromPath = (pathname: string): CommunicationsScreenKey | null => {
  if (!pathname) return null;

  if (pathname === routesPath.HOME || pathname.startsWith(`${routesPath.HOME}/`)) return 'HOME';

  if (pathname === routesPath.PAYOUTS_BALANCE || pathname.startsWith(`${routesPath.PAYOUTS_BALANCE}/`)) {
    return 'BALANCE';
  }

  if (pathname === routesPath.DISPUTES || pathname.startsWith(`${routesPath.DISPUTES}/`)) {
    return 'DISPUTE';
  }

  if (pathname === routesPath.PAYMENT_SETTINGS) {
    return 'PAYMENT_LIMITS';
  }

  if (pathname.includes('/collect-payment')) return 'COLLECT_PAYMENT';
  if (/\/share-pending-invoice\/(.+\/)?share-(qr|url)$/.test(pathname)) return 'INVOICE_COLLECT_LINK';
  if (pathname.includes('/record-payment')) return 'RECORD_PAYMENT';
  // Confirm step carries SOURCE_OF_FUNDS inline (e.g. refunds on, buffer zero).
  if (pathname.includes('/initiate-refund')) {
    return 'REFUND_CONFIRM';
  }
  if (pathname.includes('/create-refund') || pathname.includes('/issue-refund')) {
    return 'REFUND_CREATE';
  }

  if (pathname === routesPath.INVOICES_CREATE || pathname.startsWith(`${routesPath.INVOICES_CREATE}/`)) {
    return 'INVOICE_CREATE';
  }

  if (
    pathname === routesPath.ESTIMATES_CREATE ||
    pathname.startsWith(`${routesPath.ESTIMATES_CREATE}/`) ||
    matchPath(routesPath.ESTIMATES_DUPLICATE, pathname)
  ) {
    return 'ESTIMATE_CREATE';
  }

  if (
    pathname === routesPath.CREATE_JOB ||
    pathname.startsWith(`${routesPath.CREATE_JOB}/`) ||
    matchPath(routesPath.DUPLICATE_JOB, pathname)
  ) {
    return 'JOB_CREATE';
  }

  if (pathname === routesPath.CREATE_TEAM) return 'CREATE_TEAM';

  return null;
};

export const getScreenSlots = (
  screens: CommunicationsScreens | undefined,
  screenKey: CommunicationsScreenKey | null
): {
  fullPage: CommunicationsMessage | null;
  strip: CommunicationsMessage | null;
  popup: CommunicationsMessage | null;
} => {
  if (!screens || !screenKey) {
    return { fullPage: null, strip: null, popup: null };
  }

  if (screenKey === 'HOME') {
    const fullPage = screens.HOME?.fullPage ?? null;
    // Full page owns the home surface — drop strip/popup the same way the app does.
    return {
      fullPage,
      strip: fullPage ? null : screens.HOME?.strip ?? null,
      popup: fullPage ? null : screens.HOME?.popup ?? null
    };
  }

  if (screenKey === 'PAYMENT_LIMITS' || screenKey === 'PAYMENT_METHODS') {
    const limitsStrip = screens.PAYMENT_LIMITS?.strip ?? null;
    const methodsStrip = screens.PAYMENT_METHODS?.strip ?? null;
    const limitsPopup = screens.PAYMENT_LIMITS?.popup ?? null;
    const methodsPopup = screens.PAYMENT_METHODS?.popup ?? null;
    return {
      fullPage: null,
      strip: limitsStrip ?? methodsStrip,
      popup: limitsPopup ?? methodsPopup
    };
  }

  const screen = screens[screenKey] as
    | { strip?: CommunicationsMessage | null; popup?: CommunicationsMessage | null }
    | undefined;

  return {
    fullPage: null,
    strip: screen?.strip ?? null,
    popup: screen?.popup ?? null
  };
};

export const getActionControl = (
  screens: CommunicationsScreens | undefined,
  screenKey: CommunicationsScreenKey | null
): CommunicationsControl | null => {
  if (!screens || !screenKey) return null;
  const screen = screens[screenKey] as { control?: CommunicationsControl | null } | undefined;
  if (!screen || !('control' in screen)) return null;
  return screen.control ?? null;
};

export const getInlineMessage = (
  screens: CommunicationsScreens | undefined,
  screenKey: CommunicationsScreenKey | null,
  anchor: CommunicationsInlineAnchor
): CommunicationsMessage | null => {
  if (!screens || !screenKey) return null;
  const screen = screens[screenKey] as { inline?: CommunicationsInlineItem[] | null } | undefined;
  if (!screen?.inline?.length) return null;

  const match = screen.inline.find(item => item?.anchor?.toUpperCase() === String(anchor).toUpperCase());
  if (!match?.lines?.length) return null;
  return match;
};

export const shouldShowCommunicationsPopup = (
  teamId: number | null | undefined,
  popup: CommunicationsMessage | null
): boolean => {
  return Boolean(popup && teamId != null);
};

export const resolveCtaUrlPath = (ctaAction: string | null | undefined): string | null => {
  if (!ctaAction) return null;
  try {
    if (ctaAction.startsWith('/')) return ctaAction;
    const url = new URL(ctaAction);
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return null;
  } catch {
    return ctaAction.startsWith('/') ? ctaAction : null;
  }
};

export const isSameOriginUrl = (ctaAction: string): boolean => {
  try {
    const url = new URL(ctaAction);
    return typeof window !== 'undefined' && url.origin === window.location.origin;
  } catch {
    return false;
  }
};

export type CtaNavigateFn = (path: string) => void;
export type CtaOpenExternalFn = (url: string) => void;

const nonBlank = (value?: string | null): string => value?.trim() ?? '';

export const getValidCommunicationDetailRows = (details?: CommunicationsDetails | null): CommunicationsDetailRow[] => {
  if (!details?.rows?.length) return [];
  return details.rows.filter(row => nonBlank(row?.label) && nonBlank(row?.value));
};

export const hasValidCommunicationDetails = (details?: CommunicationsDetails | null): boolean =>
  getValidCommunicationDetailRows(details).length > 0;

export const getCommunicationDetailHref = (row: CommunicationsDetailRow): string | null => {
  const value = nonBlank(row.value);
  if (!value || !row.linkType) return null;
  if (row.linkType === 'PHONE') return `tel:${value}`;
  if (row.linkType === 'EMAIL') return `mailto:${value}`;
  if (row.linkType === 'URL') return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return null;
};

export const openCommunicationContact = (contact?: CommunicationsContact | null): void => {
  const value = nonBlank(contact?.value);
  if (!value) return;
  if (contact?.type === 'EMAIL' || value.includes('@')) {
    window.location.href = `mailto:${value}`;
    return;
  }
  if (contact?.type === 'PHONE') {
    window.location.href = `tel:${value}`;
  }
};

export const handleCommunicationsCta = ({
  ctaType,
  ctaAction,
  navigate,
  openExternal
}: {
  ctaType?: CommunicationsCtaType;
  ctaAction?: string | null;
  navigate: CtaNavigateFn;
  openExternal: CtaOpenExternalFn;
}): void => {
  if (ctaType === COMMUNICATIONS_CTA_TYPES.DETAILS) return;
  if (!ctaAction) return;

  if (ctaType === COMMUNICATIONS_CTA_TYPES.DEEPLINK) {
    navigate(ctaAction);
    return;
  }

  if (ctaType === COMMUNICATIONS_CTA_TYPES.EXTERNAL_URL) {
    openExternal(ctaAction);
    return;
  }

  if (ctaType === COMMUNICATIONS_CTA_TYPES.URL || !ctaType) {
    if (isSameOriginUrl(ctaAction)) {
      const path = resolveCtaUrlPath(ctaAction);
      if (path) navigate(path);
      return;
    }
    if (ctaAction.startsWith('/')) {
      navigate(ctaAction);
      return;
    }
    openExternal(ctaAction);
  }
};

export const joinMessageLines = (lines: string[] | undefined | null): string => {
  if (!lines?.length) return '';
  return lines.filter(Boolean).join(' ');
};
